import { ref, watch } from 'vue';

import { useRouter, useRoute } from 'vue-router';

import { defineStore } from 'pinia';

import ApiLicense from '@/api/ApiLicense';
import { hideLicenseAlert } from '@/state/licenseAlert';

import type {
	LicenseApiStatusKey,
	LicenseExtSourceLabel,
	LicenseGeoFencingRaw,
	LicenseQaOverlayRaw,
	LicenseQaOverlayState,
	LicenseStatusKey,
	LicenseStoreFields,
} from '@/api/ApiTypes';


const parseQaOverlay = (raw: LicenseQaOverlayRaw | null | undefined): LicenseQaOverlayState | null => {
	if (raw == null || raw.active !== true) {
		return null;
	}
	return {
		active: true,
		allowVrs: raw.allow_vrs,
		allowPppRtk: raw.allow_ppp_rtk,
		allowPppGp: raw.allow_ppp_gp,
		expiresAt: raw.expires_at,
	};
};


const LICENSE_REDIRECT_STATUSES: ReadonlySet<LicenseApiStatusKey> = new Set([
	'expired',
	'needUpdate',
]);


export const useLicenseStore = defineStore('licenseStore', () => {
	const isLoading = ref(true);
	const status = ref<LicenseStatusKey>('unknown');
	const imageId = ref<string | null>(null);
	const expirationDate = ref<string | null>(null);
	const baseStationsCount = ref<number | null>(null);
	const maxBaseStationsCount = ref<number | null>(null);
	const geoFencing = ref<LicenseGeoFencingRaw | null>(null);
	const allowVrs = ref(false);
	const allowPppRtk = ref(false);
	const allowPppGp = ref(false);
	const allowExtSources = ref<LicenseExtSourceLabel[]>([]);
	const allowPaas = ref(false);
	const allowBrdcm = ref(false);
	const featureFlagBaselines = ref(false);
	const allowBaselines = ref(false);
	const featureFlagDashboard = ref(false);
	const featureFlagCnetConstructor = ref(false);
	const featureFlagInsights = ref(false);
	const featureFlagBrdcm = ref(false);
	const featureFlagJamming = ref(false);
	const nearConnectionsLimit = ref(0);
	const vrsConnectionsLimit = ref(0);
	const qaOverlay = ref<LicenseQaOverlayState | null>(null);

	const router = useRouter();
	const route = useRoute();

	const update = (withoutRedirect: boolean | undefined, props: Partial<LicenseStoreFields>): void => {
		status.value = props.status ?? 'unknown';
		imageId.value = props.imageId ?? null;
		expirationDate.value = props.expirationDate ?? null;
		baseStationsCount.value = props.baseStationsCount ?? 0;
		maxBaseStationsCount.value = props.maxBaseStationsCount ?? null;
		geoFencing.value = props.geoFencing ?? null;
		allowVrs.value = props.allowVrs || false;
		allowPppRtk.value = props.allowPppRtk || false;
		allowPppGp.value = props.allowPppGp || false;
		allowExtSources.value = props.allowExtSources || [];
		allowPaas.value = props.allowPaas || false;
		allowBrdcm.value = props.allowBrdcm || false;
		featureFlagBaselines.value = props.featureFlagBaselines || false;
		allowBaselines.value = props.allowBaselines || false;
		featureFlagDashboard.value = props.featureFlagDashboard || false;
		featureFlagCnetConstructor.value = props.featureFlagCnetConstructor || false;
		featureFlagInsights.value = props.featureFlagInsights || false;
		featureFlagBrdcm.value = props.featureFlagBrdcm || false;
		featureFlagJamming.value = props.featureFlagJamming || false;
		nearConnectionsLimit.value = props.nearConnectionsLimit || 0;
		vrsConnectionsLimit.value = props.vrsConnectionsLimit || 0;
		qaOverlay.value = props.qaOverlay ?? null;
		hideLicenseAlert();
		if (
			props.status != null
			&& props.status !== 'unknown'
			&& LICENSE_REDIRECT_STATUSES.has(props.status)
			&& !withoutRedirect
		) {
			router.push({ name: 'license-alert-page' });
		}
	};

	const LICENSE_ROUTE_GUARDS = [
		{ routes: ['dashboard'] as const, flag: (): boolean => featureFlagDashboard.value },
		{
			routes: ['cnet-constructor', 'cnet-constructor-edit', 'cnet-constructor-add'] as const,
			flag: (): boolean => featureFlagCnetConstructor.value && allowPppGp.value,
		},
		{
			routes: ['baselines', 'baselines-edit', 'add-baseline'] as const,
			flag: (): boolean => allowBaselines.value,
		},
	] as const;

	const checkRoute = (routeName: string | symbol | null | undefined): void => {
		if (status.value === 'unknown') {
			return;
		}
		if (typeof routeName !== 'string') {
			return;
		}
		const failedCheck = LICENSE_ROUTE_GUARDS.find(
			check => (check.routes as readonly string[]).includes(routeName) && !check.flag(),
		);
		if (failedCheck) {
			router.push({ name: 'not-found-page' });
		}
	};

	watch(() => route.name, checkRoute);

	const refresh = (callback?: (() => void) | null, withoutRedirect?: boolean): void => {
		isLoading.value = true;
		ApiLicense.licenseStatusGet(data => {
			isLoading.value = false;
			if (data.status === 'ok') {
				update(
					withoutRedirect,
					{
						status: data.license_status,
						imageId: data.image_id ?? null,
						expirationDate: data.expiration_date ?? null,
						baseStationsCount: data.base_stations_count ?? null,
						maxBaseStationsCount: data.max_base_stations_count ?? null,
						geoFencing: data.geo_fencing ?? null,
						allowVrs: data.allow_vrs ?? false,
						allowPppRtk: data.allow_ppp_rtk ?? false,
						allowPppGp: data.allow_ppp_gp ?? false,
						allowExtSources: data.allow_ext_sources ?? [],
						allowPaas: data.allow_paas ?? false,
						allowBrdcm: data.allow_brdcm ?? false,
						featureFlagBaselines: data.feature_flag_baselines ?? false,
						allowBaselines: data.allow_baselines ?? false,
						featureFlagDashboard: data.feature_flag_dashboard ?? false,
						featureFlagCnetConstructor: data.feature_flag_cnet_constructor ?? false,
						featureFlagInsights: data.feature_flag_insights ?? false,
						featureFlagBrdcm: data.feature_flag_brdcm ?? false,
						featureFlagJamming: data.feature_flag_jamming ?? false,
						nearConnectionsLimit: data.near_connections_limit ?? 0,
						vrsConnectionsLimit: data.vrs_connections_limit ?? 0,
						qaOverlay: parseQaOverlay(data.qa_overlay),
					},
				);
				if (callback != null) {
					callback();
				}
				checkRoute(route.name);
			} else {
				update(withoutRedirect, { status: 'expired' });
			}
		});
	};

	return {
		isLoading,
		status,
		imageId,
		expirationDate,
		baseStationsCount,
		maxBaseStationsCount,
		geoFencing,
		allowVrs,
		allowPppRtk,
		allowPppGp,
		allowExtSources,
		allowPaas,
		allowBrdcm,
		featureFlagBaselines,
		allowBaselines,
		featureFlagDashboard,
		featureFlagCnetConstructor,
		featureFlagInsights,
		featureFlagBrdcm,
		featureFlagJamming,
		nearConnectionsLimit,
		vrsConnectionsLimit,
		qaOverlay,
		refresh,
	};
});
